import {
  planned,
  producing,
  speedLoss,
  unplanned,
  untagged,
  waiting,
} from "reports/constants/colors";
import { PnlBreakdown } from "reports/getters/pnlBreakdownGetter/pnlBreakdownTypes";
import { PnlCategoriesEnum, PnlCategoryLabelsEnum } from "reports/types";
import { MAX_BAR_SIZE } from "../GenericCharts/BarCharts/GenericComposedChart";
import { Bar } from "recharts";
import { RechartToolTipStyles } from "../RechartHelpers/TooltipStyles";
import StackedPnLBarChart from "./Charts/StackedPnLBarChart";
import { MetricEnum } from "reports/utils/metricTypes";
import {
  CHART_HEIGHT,
  CHART_WIDTH,
  pnlBreakdownsToProductionPnlTimesChartData,
} from "./bespokeReportUtils";
import { useTranslation } from "react-i18next";

export default function MonthlyPerformanceChart({
  width = CHART_WIDTH,
  height = CHART_HEIGHT,
  pnlBreakdowns,
  shouldWrapInCard = true,
}: {
  width?: number;
  height?: number;
  pnlBreakdowns: PnlBreakdown[];
  shouldWrapInCard?: boolean;
}) {
  const { t } = useTranslation("EOSReport");

  const { chartData } = pnlBreakdownsToProductionPnlTimesChartData({
    t,
    pnlBreakdowns,
    chartDataType: "M",
    metricTitle: t(MetricEnum.OEE),
  });

  const { bars, customBars } = getPnlCustomBars();
  const tooltipContent = customTooltipContent(bars, chartData);
  return (
    <StackedPnLBarChart
      {...{
        chartData,
        customBars,
        tooltipContent,
        width,
        height,
        shouldWrapInCard,
      }}
      xLabel={"Months"}
      yLabel={"Time (%)"}
    />
  );
}

/**
 * Get list of bars and bar objects for Production Pnl Categories
 *
 * @return {*}
 *  bars: list of bars
 *  customBars: list of Bar rechart objects with label
 */
function getPnlCustomBars() {
  const bars = [
    {
      dataKey: "metric",
      name: MetricEnum.OEE,
      stackId: "Primary",
      fill: producing,
      label: true,
    },
    {
      dataKey: PnlCategoriesEnum.planned,
      name: PnlCategoryLabelsEnum.planned,
      stackId: "Primary",
      fill: planned,
      label: false,
    },
    {
      dataKey: "speedLoss",
      name: MetricEnum.SPEED_LOSS_PERCENT,
      stackId: "Primary",
      fill: speedLoss,
      label: false,
    },

    {
      dataKey: PnlCategoriesEnum.waiting,
      name: PnlCategoryLabelsEnum.waiting,
      stackId: "Primary",
      fill: waiting,
      label: false,
    },
    {
      dataKey: PnlCategoriesEnum.unplanned,
      name: PnlCategoryLabelsEnum.unplanned,
      stackId: "Primary",
      fill: unplanned,
      label: false,
    },
    {
      dataKey: PnlCategoriesEnum.unknown,
      name: PnlCategoryLabelsEnum.unknown,
      stackId: "Primary",
      fill: untagged,
      label: false,
    },
  ];

  const customBars = bars.map((bar, index) => {
    return <Bar key={bar.dataKey} {...bar} maxBarSize={MAX_BAR_SIZE}></Bar>;
  });

  return { bars, customBars };
}

/**
 * Custom tooltip content rather than default
 *
 * @param {*} bars
 * @param {*} chartData
 */
const customTooltipContent =
  (bars, chartData) =>
  ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      const payloadDate = payload[0].payload.date;
      const chartDataForMonth = chartData.find(
        ({ date }) => date === payloadDate
      );

      return (
        <RechartToolTipStyles>
          <div className="tooltip-title">{label}</div>
          {bars.map(({ name, dataKey }) => {
            return (
              <div
                key={dataKey}
              >{`${name}: ${chartDataForMonth?.[dataKey]}%`}</div>
            );
          })}
        </RechartToolTipStyles>
      );
    }

    return null;
  };
